import { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { apiFetch } from '../../fetchInterceptor';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Upload,
  FileText,
  Download,
  Clock,
  CheckCircle2,
  Loader2,
  File,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { downloadPdfBlob } from '../../lib/downloadPdfBlob';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusStyles = {
  pending_review: { label: 'Pending Review', icon: Clock, color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  reviewed: { label: 'Reviewed', icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
};

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function ClientDocuments() {
  const { portalInfo } = useOutletContext() || {};
  const firmName = portalInfo?.workspace_name || 'them';
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [description, setDescription] = useState('');
  const fileRef = useRef(null);

  const fetchDocs = async () => {
    try {
      const res = await apiFetch(`${API_URL}/api/client-portal/documents`);
      if (res.ok) {
        const data = await res.json();
        setDocuments(data.documents || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchDocs(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10MB)');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', description);
      const res = await apiFetch(`${API_URL}/api/client-portal/documents`, {
        method: 'POST',
        body: formData,
      });
      if (res.ok) {
        toast.success('Document uploaded successfully');
        setDescription('');
        if (fileRef.current) fileRef.current.value = '';
        fetchDocs();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Upload failed');
      }
    } catch (e) { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await apiFetch(`${API_URL}/api/client-portal/documents/${doc.document_id}/download`);
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Download failed');
      }
      const blob = await res.blob();
      await downloadPdfBlob(blob, doc.filename || 'document.pdf', { mode: 'download' });
    } catch (e) {
      toast.error(e?.message || 'Download failed');
    }
  };

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-64" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="client-documents">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Documents</h1>
        <p className="text-muted-foreground text-sm mt-1">Upload receipts, documents, and other files for {firmName} to review.</p>
      </div>

      {/* Upload Area */}
      <Card className="border-dashed border-2 hover:border-emerald-300 dark:hover:border-emerald-700 transition-colors">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
              <Upload className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <p className="font-medium">Upload a Document</p>
              <p className="text-xs text-muted-foreground mt-0.5">PDF, images, or spreadsheets up to 10MB</p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Description (optional)"
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="w-48"
                data-testid="doc-description-input"
              />
              <Button
                variant="outline"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="upload-doc-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Choose File
              </Button>
              <input ref={fileRef} type="file" className="hidden" onChange={handleUpload} accept=".pdf,.png,.jpg,.jpeg,.xlsx,.csv,.doc,.docx" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Document List */}
      {documents.length > 0 ? (
        <div className="grid gap-3">
          {documents.map(doc => {
            const st = statusStyles[doc.status] || statusStyles.pending_review;
            const StIcon = st.icon;
            return (
              <Card key={doc.document_id}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                    <File className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{doc.filename}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-muted-foreground">{formatSize(doc.size)}</span>
                      {doc.description && <span className="text-xs text-muted-foreground">- {doc.description}</span>}
                      <span className="text-xs text-muted-foreground">{new Date(doc.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <Badge className={cn("text-[10px] gap-1", st.color)}>
                    <StIcon className="w-3 h-3" />
                    {st.label}
                  </Badge>
                  <Button variant="ghost" size="icon" onClick={() => handleDownload(doc)} data-testid={`download-doc-${doc.document_id}`}>
                    <Download className="w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <FileText className="w-10 h-10 mx-auto mb-2 text-muted-foreground/50" />
            <p className="text-muted-foreground">No documents yet</p>
            <p className="text-xs text-muted-foreground mt-1">Upload your first document above</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
